import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import '@lmt/unicorn-shared/app/components/global-forms/GlobalFormContent.scss';
import { backendUri } from '@lmt/unicorn-shared/app/auth/openid/openid-settings';
import { OpenIdCallback } from '@lmt/unicorn-shared/app/auth/openid/OpenIdCallback';
import { ModalProvider } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import 'devextreme/dist/css/dx.light.css';
import { i18n } from 'i18next';
import React, { useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import '../app.global.css';
import '../App.global.scss';
import { Loading } from './components/common/Loading';
import { FeatureFlags } from './constants/featureFlags';
import './helpers/cached-images';
import { MiniProfiler } from './helpers/MiniProfiler';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QuerySuspenseErrorWrapper } from './helpers/retry-helper';
import { AppRouter } from './Router';
import { initialize } from './initialize';
import { LanguageProvider } from './helpers/localization/LanguageProvider';
import { logOut, setAuthData, useClaims } from './features/auth/operations';
import { MuiProvider } from './helpers/MuiProvider';
export type ConfigureAppOptions = {
  backendStartFailed: boolean;
};
import config from 'devextreme/core/config';
config({
  licenseKey:
    'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogImFhNTlmMjNiLTBhNzAtNDFkOS1iN2NlLWYxZjZlY2IzZDIxZiIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjQxCn0=.ctmGscOk+7cJSSHtsy5AI76Y/EGuMkdCxJpICaWs8mFqkRN6RFjfnEZprK2kcjdb92beUka22rntOfPX4rS+Xhf6o4G8wCFgb9n5cK7m7fnEqGxeubtVp9zDnt8EmtAjVJj2fw==',
});

export const App = (props: {
  appType: 'desktop' | 'web';
  additionalComponents?: React.FC;
  configureTranslation?: (translation: i18n) => void;
  backendStartFailed: boolean;
  // initializeResult: ReturnType<typeof initialize>;
}) => {
  // const { store, persistor, queryClient } = props.initializeResult;

  const username = useClaims()?.name ?? 'root';
  const { store, persistor, queryClient } = useMemo(() => {
    return initialize(username);
  }, [username]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  return (
    <OpenIdCallback
      signInRedirectHandler={(user) => {
        setAuthData({
          access_token: user.access_token,
          refresh_token: user.refresh_token!,
          claims: user.profile as any,
        });
        window.history.pushState(null, '', backendUri);
      }}
      signOutRedirectHandler={() => {
        logOut();
      }}
      loading={<Loading loading={true} />}
    >
      <QueryClientProvider client={queryClient}>
        {/* <ReactQueryDevtools /> */}
        <Provider store={store} key={username}>
          <PersistGate
            loading={<Loading loading={true} />}
            persistor={persistor}
          >
            <LanguageProvider init={props.configureTranslation}>
              <MuiProvider>
                <ModalProvider>
                  <DndContext sensors={sensors}>
                    <QuerySuspenseErrorWrapper>
                      <AppRouter
                        key={username}
                        {...props}
                        initializeResult={{ store, persistor, queryClient }}
                      />
                    </QuerySuspenseErrorWrapper>
                  </DndContext>
                </ModalProvider>
              </MuiProvider>
            </LanguageProvider>
          </PersistGate>
        </Provider>

        {FeatureFlags.EnableMiniProfiler && <MiniProfiler />}
      </QueryClientProvider>
    </OpenIdCallback>
  );
};
